import { groupBy } from "lodash";
import { Claim } from "services/KnowfullyAdminClient";
import { AuthStore } from "stores/authStore";
import { ClaimType } from "types/claimTypes";

export type ApplicationClaimGroups = {
  grouped: Record<string, Claim[]>;
  ungrouped: Claim[];
};

export type ClaimsByApplication = Record<string, ApplicationClaimGroups>;

export const createClaimNodeId = {
  app: (appName: string) => `app-${appName}`,
  group: (appName: string, groupName: string) => `group-${appName}-${groupName}`,
  claim: (claimId: string) => `claim-${claimId}`,
};

export const groupClaimsByApplication = (claims: Claim[]): ClaimsByApplication => {
  const byApp = groupBy(claims, (c) => c.applicationName ?? "");
  const result: ClaimsByApplication = {};

  for (const [appName, appClaims] of Object.entries(byApp)) {
    const grouped: Record<string, Claim[]> = {};
    const ungrouped: Claim[] = [];

    for (const claim of appClaims) {
      if (claim.groupName) {
        grouped[claim.groupName] = grouped[claim.groupName] ?? [];
        grouped[claim.groupName].push(claim);
      } else {
        ungrouped.push(claim);
      }
    }

    result[appName] = { grouped, ungrouped };
  }

  return result;
};

export const getGroupsWithSelectedClaims = (
  appGroups: ApplicationClaimGroups,
  selectedClaimIds: Set<string>
): string[] => {
  return Object.entries(appGroups.grouped)
    .filter(([, claims]) => claims.some((c) => c.id && selectedClaimIds.has(c.id)))
    .map(([groupName]) => groupName);
};

export const hasSelectedClaimsInApplication = (
  appGroups: ApplicationClaimGroups,
  selectedClaimIds: Set<string>
): boolean => {
  const allClaims = [
    ...Object.values(appGroups.grouped).flat(),
    ...appGroups.ungrouped,
  ];
  return allClaims.some((c) => c.id && selectedClaimIds.has(c.id));
};

export const calculateInitialExpandedClaimNodes = (
  claimsByApp: ClaimsByApplication,
  selectedClaimIds: Set<string>,
  isCreateMode: boolean
): string[] => {
  if (isCreateMode || selectedClaimIds.size === 0) {
    return [];
  }

  const expandedNodes: string[] = [];

  for (const [appName, appGroups] of Object.entries(claimsByApp)) {
    if (!hasSelectedClaimsInApplication(appGroups, selectedClaimIds)) {
      continue;
    }

    expandedNodes.push(createClaimNodeId.app(appName));

    const groupsWithSelections = getGroupsWithSelectedClaims(appGroups, selectedClaimIds);
    for (const groupName of groupsWithSelections) {
      expandedNodes.push(createClaimNodeId.group(appName, groupName));
    }
  }

  return expandedNodes;
};

export const hasUserPermission = (
  parentClaim: string,
  claimResource: string,
  claimPermission: string
) => {
  const userRaw = global.localStorage.getItem("user");
  if (!userRaw) {
    return false;
  }

  const userData = JSON.parse(userRaw) as AuthStore;
  if (!userData) {
    return false;
  }

  return userData.claims?.some(
    (c) =>
      c?.claimType === ClaimType.Permission &&
      c?.parentClaim === parentClaim &&
      c?.claimResource === claimResource &&
      c?.claimPermission === claimPermission
  );
};
